<template>
  <BlockContainer v-if="isViewReady" class="addendum p-4">
    <hl-ModalV2
      v-if="isDeleteAddendumModalVisible"
      title="Slett tilleggskontrakt"
      titleIcon="budicon-alert-sign"
      is-closable
      @close="isDeleteAddendumModalVisible = false"
    >
      <p>Er du sikker på at du vil slette tilleggskontrakten?</p>

      <template v-slot:buttons>
        <hl-Button variant="secondary" outline @click="isDeleteAddendumModalVisible = false"> Avbryt </hl-Button>

        <hl-Button variant="danger" @click="deleteAddendum(addendumIdMarkedForDeletion)">
          <div class="d-flex justify-content-center align-items-center">
            <hl-LoaderDots v-if="isDeletingAddendum" variant="white" class="mr-2" />
            <span>Slett</span>
          </div>
        </hl-Button>
      </template>
    </hl-ModalV2>

    <div class="addendum__content">
      <h5>Tilleggskontrakt</h5>

      <div v-if="isFetchingAddendums" class="d-flex justify-content-center mt-5">
        <hl-LoaderDots size="large" />
      </div>

      <div v-else-if="addendums.length">
        <h6 class="mt-5">Opprettet tilleggskontrakter</h6>

        <div class="addendum__content__cards mt-3">
          <div v-for="addendum in addendums" :key="addendum.id" class="addendum__content__cards__card">
            <div class="addendum__content__cards__card__row">
              <span>Ikrafttredelsesdato</span>
              <strong>{{ addendum.effectiveDate }}</strong>
            </div>

            <div class="addendum__content__cards__card__row mt-3">
              <span>Sluttdato</span>
              <strong>{{ addendum.endDate }}</strong>
            </div>

            <div class="addendum__content__cards__card__row mt-3">
              <span>Status</span>
              <strong>{{ addendumStatusText(addendum.id) }}</strong>
            </div>

            <div class="addendum__content__cards__card__row mt-3">
              <span>Husleie</span>
              <strong>{{ addendum.amount }}</strong>
            </div>

            <div class="addendum__content__cards__card__row mt-3">
              <span>Leietaker</span>
              <strong>{{ addendum.tenant.fullName }}</strong>
            </div>

            <div v-if="addendum.status !== 'signed'" class="addendum__content__cards__card__buttons mt-3">
              <hl-Button variant="danger" size="sm" @click="openDeleteAddendumModal(addendum.id)"> Slett </hl-Button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!isTenancyContractStatusDraft" class="mt-5">
        <h6>Opprett en tilleggskontrakt</h6>

        <p>
          Dersom du ønsker å gjøre endringer i kontrakten eller forlenge leieforholdet kan du opprette en
          tilleggskontrakt.
        </p>

        <hl-Button :href="addendumLink" target="_blank" rel="noopener" class="mt-2">
          Opprett tilleggskontrakt
        </hl-Button>
      </div>
    </div>
  </BlockContainer>
</template>

<script>
import Vue from 'vue';
import BlockContainer from '@/components/BlockContainer';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import HlDate from '@modules/husleie-framework/src/components/time/Date.vue';
import LoaderDots from '@modules/husleie-framework/src/components/loaders/LoaderDots';
import { AppRedirect } from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import { ContractApi } from '@modules/husleieapi2';
import ModalV2 from '@modules/husleie-framework/src/components/modals/ModalV2.vue';

export default Vue.extend({
  name: 'TenancyAddendum',
  data() {
    return {
      isViewReady: false,
      addendums: [],
      isFetchingAddendums: false,
      addendumIdMarkedForDeletion: undefined,
      isDeleteAddendumModalVisible: false,
      isDeletingAddendum: false,
      isTenancyContractStatusDraft: false,
    };
  },
  props: {
    tenancyId: {
      type: Number,
      required: true,
    },
    contractSessionId: {
      type: String,
      required: false,
    },
  },
  components: {
    BlockContainer,
    'hl-Date': HlDate,
    'hl-LoaderDots': LoaderDots,
    'hl-Button': Button,
    'hl-ModalV2': ModalV2,
  },
  computed: {
    addendumLink() {
      return RedirectOperations.getURL(AppRedirect.ADDENDUM_LANDLORD, {
        tenancyId: this.tenancyId,
      });
    },
  },
  methods: {
    addendumStatusText(addendumId) {
      const addendum = this.addendums.find(addendum => addendum.id === addendumId);

      return addendum.status !== 'signed' ? 'Venter på signatur(er)' : 'Signert';
    },
    async fetchAddendums() {
      const api = new ContractApi();

      this.isFetchingAddendums = true;
      try {
        this.addendums = (await api.listContractAddendumsFromTenancy(this.tenancyId))?.addendums || [];
        this.isTenancyContractStatusDraft = this.addendums.some(
          addendum => addendum.tenancyContractStatus === 'draft'
        );
      } catch {
        this.$toast('Kunne ikke laste tilleggskontrakter', 'danger');
      } finally {
        this.isFetchingAddendums = false;
      }
    },
    async openDeleteAddendumModal(addendumId) {
      this.addendumIdMarkedForDeletion = addendumId;
      this.isDeleteAddendumModalVisible = true;
    },
    async deleteAddendum(addendumId) {
      this.isDeletingAddendum = true;

      try {
        const api = new ContractApi();
        await api.deleteContractAddendum(addendumId);

        this.$toast('Tilleggskontrakten ble slettet', 'success');
      } catch {
        this.$toast('Et problem oppstod med å slette tilleggskontrakten. Vennligst prøv igjen', 'danger');
      }

      this.isDeletingAddendum = false;
      this.isDeleteAddendumModalVisible = false;

      await this.fetchAddendums();
    },
  },
  async created() {
    await this.fetchAddendums();

    this.isViewReady = true;
  },
});
</script>

<style lang="scss" scoped>
.addendum__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.addendum__content__cards {
  display: flex;
  flex-direction: column;
  gap: 1rem 0rem;
}
.addendum__content__cards__card {
  border: 1px solid $gray-200;
  border-radius: 8px;
  padding: 1rem;
}
.addendum__content__cards__card__row {
  display: grid;
  justify-items: center;
  grid-template-columns: 1fr;
}
.addendum__content__cards__card__buttons button {
  width: 100%;
}

@media (min-width: 400px) {
  .addendum__content {
    display: initial;
    text-align: initial;
  }
  .addendum__content__cards__card__row {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .addendum__content__cards__card__buttons {
    display: flex;
    justify-content: flex-end;
  }
  .addendum__content__cards__card__buttons button {
    width: fit-content;
  }
}
</style>
